import type { ApolloCache, ApolloClient, Reference } from "@apollo/client";

import type {
  GetIssueQuery,
  GetIssueQueryVariables,
  MarkIssueAsSeenMutation,
} from "@medbillai/generated-graphql-types";

import type { PillProps, TabsTabOptions } from "@medbillai/ui";

import { reportUnhandledVariant } from "../../../lib/sentry";
import { getUnreadIssuesCountQuery } from "../../../provider/issue-events/IssueEventsProvider";
import { getIssueQuery } from "../../case/utils/queries";
import type { BaseEvent, IssueMeRow } from "../../case/utils/types";
import { getIssuesQueryV2 } from "./queries";
import type { CaseListFilters, Issue, IssueStatus } from "./types";

export const pageSize = 15;

export const filterOptionIds = {
  open: "open",
  unread: "unread",
  read: "read",
  closed: "closed",
};

export const filterOptions: TabsTabOptions = [
  { id: filterOptionIds.open, name: "Open" },
  { id: filterOptionIds.unread, name: "Unread" },
  { id: filterOptionIds.closed, name: "Closed" },
];

export const filterOptionIdFor = ({ showRead, showClosed }: CaseListFilters) =>
  showClosed
    ? filterOptionIds.closed
    : showRead
      ? filterOptionIds.open
      : filterOptionIds.unread;

export const formatEvent = (event: Issue["lastEvent"]) => {
  if (!event) {
    return "";
  }
  switch (event.__typename) {
    case "issue__ChatEvent_Type":
      return event.chatBody;
    case "issue__ClosedEvent_Type":
      return "Issue closed";
    case "issue__DocumentUploadedEvent_Type":
      return "Document uploaded";
    case "issue__ReopenedEvent_Type":
      return "Issue reopened";
    case "issue__HipaaReleaseRequestedEvent_Type":
      return "HIPAA release requested";
    case "issue__TemplateReleaseRequestedEvent_Type":
      return `${event.template.description} requested`;
    case "issue__BillDisputeEvent_Type":
      return "Bill disputed";
    case "issue__PaymentAgreementRequestedEvent_Type":
      return "We found potential savings!";
    case "issue__ClosurePaymentRequestedEvent_Type":
      return "Payment Accepted";
    case "issue__BenefitsSummaryGeneratedEvent_Type":
      return "Benefits summary generated";
    case "issue__PlanSelectionEvent_Type":
      return "Select plan";
    case "issue__UserSelectionRequestedEvent_Type":
      return (
        event.stringMessage ?? "Here are some options you can choose from:"
      );
    case "issue__UserMultiSelectionRequestedEvent_Type":
      return event.multiMessage ?? "Here are some options you can choose from:";
    case "issue__UserInputRequestedEvent_Type":
      return "Respond to request";
    case "issue__ConnectInsuranceRequestedEvent_Type":
      return (
        event.insuranceLinkMessage ??
        "To start getting personalized help, connect your insurance."
      );
    case "issue__ConnectAccountRequestedEvent_Type":
      return "To start getting personalized help, connect your account.";
    case "issue__BillPaymentRequestedEvent_Type":
      return "Bill payment requested";
    case "issue__BillPaymentSuccessEvent_Type":
      return "Payment completed";
    case "issue__DocumentAnalysisEvent_Type":
      return "Document analysis";
    case "issue__BillPayRequestedEvent_Type":
      return "We found potential savings!";
    case "issue__BillPaySuccessEvent_Type":
      return "Payment completed";
    case "issue__PostEscalationActionsEvent_Type":
      return "Complete required steps";
    case "issue__HAAssignedEvent_Type":
      return "Healthcare Advocate assigned";
    case "issue__DocumentClassificationFailedEvent_Type":
      return "We can't read this document";
    case "issue__PaymentChargeFailedEvent_Type":
      return "Payment didn't go through";
    case "issue__FinancialAssistanceFlowEvent_Type":
      return "See if you qualify to lower this bill";
    case "issue__PaymentPreferencesRequestedEvent_Type":
      return "Set your payment preferences";
    case "issue__OutreachEvent_Type":
      return "Reminder";
    case "issue__ProviderSearchEvent_Type":
      return "Provider search";
    case "issue__UiBlocksEvent_Type":
      // The card's first line, e.g. its title.
      return event.blocksFallbackText?.split("\n")[0] || "Update on your case";
    case "issue__AskReraisedEvent_Type":
      return "Pick up where you left off";
    // These events are not visible to the user, so they shouldn't be
    // returned in userVisibleEvents and thus we don't need to format them.
    case "issue__CommentEvent_Type":
    case "issue__PaymentChargeResolvedEvent_Type":
    case "issue__InternalDocumentUploadedEvent_Type":
    case "issue__ToolExecutionEvent_Type":
    case "issue__PaymentPreferencesUpdatedEvent_Type":
    // PhoneCallEvent and CorrespondenceEvent are staff-only; they never reach
    // the patient app.
    case "issue__CorrespondenceEvent_Type":
    case "issue__PhoneCallEvent_Type":
      return "";
    case "issue__CardCollectionRequestedEvent_Type":
      return "Add a card for your success fee";
    default:
      reportUnhandledVariant(event, "formatEvent");
      return "";
  }
};

export const cleanMarkdownText = (text: string | null) => {
  if (!text) {
    return "";
  }
  return text.replace(/[\\^$*+?.()|[\]{}#]/g, "");
};

/**
 * Optimistically updates the cache so that the notification statuses are synced.
 */
export function updateIssueAsSeenInCache(
  cache: ApolloCache<unknown>,
  issueId: string,
) {
  // Clear firstUnseenEvent on the normalized Issue entity so every query that
  // selects it (GetIssue, cases list, home, etc.) stops seeing unread state —
  // and so the case-screen MarkIssueAsSeen effect does not re-fire.
  cache.modify({
    id: cache.identify({ __typename: "issue__Issue_Type", id: issueId }),
    fields: {
      firstUnseenEvent() {
        return null;
      },
      // Issue.actions holds unseen staff chats *and* action-required events
      // (dbschema/issue.gel); seeing the case drops the unseen-only ones
      // server-side. Mirror that here or cache-first readers — the home "New
      // message" badge — keep flagging a message the user just read.
      actions(existing: readonly Reference[] = [], { readField }) {
        return existing.filter(action => readField("actionRequired", action));
      },
    },
  });

  const cachedData = cache.readQuery({
    query: getIssuesQueryV2,
  });

  if (cachedData?.queryIssuesV2) {
    const updatedIssues = cachedData.queryIssuesV2.map(issue => {
      if (issue.id === issueId) {
        return {
          ...issue,
          firstUnseenEvent: null,
        };
      }
      return issue;
    });

    cache.writeQuery({
      query: getIssuesQueryV2,
      data: {
        ...cachedData,
        queryIssuesV2: updatedIssues,
      },
    });
  }

  updateUnreadIssuesCount(cache, issueId);
}

function updateUnreadIssuesCount(cache: ApolloCache<unknown>, issueId: string) {
  const cachedUnreadIssuesCount = cache.readQuery({
    query: getUnreadIssuesCountQuery,
  });

  if (!cachedUnreadIssuesCount?.me) {
    return;
  }

  const updatedIssues =
    cachedUnreadIssuesCount.me.issues?.filter(issue => issue.id !== issueId) ||
    [];

  cache.writeQuery({
    query: getUnreadIssuesCountQuery,
    data: {
      ...cachedUnreadIssuesCount,
      me: {
        ...cachedUnreadIssuesCount.me,
        issues: updatedIssues,
      },
    },
  });
}

export function handleUpdateForSeenIssue(
  cache: ApolloCache<unknown>,
  data: MarkIssueAsSeenMutation | null | undefined,
) {
  if (!data) {
    return;
  }
  const issueId = data.updateIssue?.id;
  if (issueId) {
    updateIssueAsSeenInCache(cache, issueId);
  }
}

export function getIssueStatus({
  closedAt,
  firstUnseenEventId,
}: {
  closedAt?: string | null;
  firstUnseenEventId?: string | null;
}): "CLOSED" | "NEW_ACTIVITY" | "PROCESSING" {
  return firstUnseenEventId
    ? "NEW_ACTIVITY"
    : closedAt
      ? "CLOSED"
      : "PROCESSING";
}

export const getChatPillStyle = (status: IssueStatus): PillProps => {
  switch (status) {
    case "CLOSED":
      return { text: "Closed", notificationDot: false, theme: "gray" };
    case "NEW_ACTIVITY":
      return {
        text: "New",
        notificationDot: true,
        theme: "green",
      };
    default:
      return {
        text: "Open",
        notificationDot: false,
        theme: "lightblue",
      };
  }
};

export type CacheWriteResult = "modified" | "cache-miss";

const modifyDocumentUploadEvent = (
  client: ApolloClient<object>,
  eventId: string,
  fields: { uploading: boolean; uploadFailed: boolean },
): CacheWriteResult => {
  const cacheId = client.cache.identify({
    __typename: "issue__DocumentUploadedEvent_Type",
    id: eventId,
  });
  // cache.identify is a pure key-builder; check the normalized store for the
  // entity. cache.modify's boolean return conflates "missing" with "no field
  // changed", and @client read-only fields aren't materialized on the
  // storeObject until written, so modify can return false on the first call
  // against a freshly-fetched event.
  if (!cacheId || !Object.hasOwn(client.cache.extract(), cacheId)) {
    return "cache-miss";
  }
  client.cache.modify({
    id: cacheId,
    fields: {
      uploading: () => fields.uploading,
      uploadFailed: () => fields.uploadFailed,
    },
  });
  return "modified";
};

export const markUploadEventStarting = (
  client: ApolloClient<object>,
  eventId: string,
): CacheWriteResult =>
  modifyDocumentUploadEvent(client, eventId, {
    uploading: true,
    uploadFailed: false,
  });

export const markUploadEventFailed = (
  client: ApolloClient<object>,
  eventId: string,
): CacheWriteResult =>
  modifyDocumentUploadEvent(client, eventId, {
    uploading: false,
    uploadFailed: true,
  });

export const addEventToGetIssueQueryCache = ({
  client,
  variables,
  newEvent,
}: {
  client: ApolloClient<object>;
  variables: GetIssueQueryVariables;
  newEvent: BaseEvent;
}): CacheWriteResult => {
  const existingData = client.readQuery<GetIssueQuery>({
    query: getIssueQuery,
    variables,
  });
  const existingIssue = existingData?.issue__Issue?.[0];
  if (!existingData || !existingIssue) {
    return "cache-miss";
  }
  const newIssueData = {
    ...existingIssue,
    userVisibleEvents: [...(existingIssue.userVisibleEvents ?? []), newEvent],
    lastEvent: newEvent,
  };
  client.writeQuery({
    query: getIssueQuery,
    variables,
    data: {
      __typename: "Query",
      issue__Issue: [newIssueData],
      me:
        existingData.me ??
        ({
          __typename: "User_Type",
          id: variables.userId,
          webAgentLinks: [],
        } satisfies IssueMeRow),
    },
  });
  return "modified";
};
